import { useIdleTimer } from 'react-idle-timer';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Honeybadger } from '@honeybadger-io/react';
import { useQueryClient } from '@tanstack/react-query';

import { requestApi } from 'api/Api';

import { isKioskDevice } from 'lib/helpers/kiosk';

import Modal from 'components/shared/Modal/Modal';
import Button from 'components/shared/Button/Button';
import NotFound from 'components/practice/Dashboard/components/NotFound';

// Patient screens a kiosk hands off to that live outside the /kiosk tree: instant-action
// links, the check-in wizard, and the public invoice/receipt page. A kiosk check-in starts
// as /ia/<link> and then runs entirely on /checkin/*, and pushed payments land on
// /invoice/<token>, so without these the timer stops at the handoff.
const HANDOFF_PREFIXES = ['/ia/', '/instant-packet', '/checkin', '/invoice/'];

const isHandoffScreen = (pathname) =>
  HANDOFF_PREFIXES.some((prefix) => pathname.startsWith(prefix));

// The check-in wizard is mounted at /checkin/*, /mobile/checkin/* and /kiosk/checkin/*, so a
// prefix match on any single mount misses the other two.
const isCheckinScreen = (pathname) => pathname.includes('/checkin');

// Filling out a check-in takes real reading and typing time, so it gets a far longer
// budget than the kiosk's own short landing screens.
const CHECKIN_IDLE_SECONDS = 180;
// A card-present sale is driven from the terminal, not the glass: the patient is touching the
// reader, which generates no activity here. The reader's own prompts time out well inside this
// budget, so in practice only a finished/failed/canceled screen left sitting reaches it.
const PAYMENT_IDLE_SECONDS = 240;
const KIOSK_IDLE_SECONDS = 60;
const MOBILE_IDLE_SECONDS = 600;
const WARNING_SECONDS = 30;

const resolveIdleSeconds = (pathname) => {
  if (pathname.startsWith('/kiosk/payment')) return PAYMENT_IDLE_SECONDS;
  if (pathname.startsWith('/mobile/')) return MOBILE_IDLE_SECONDS;
  if (isCheckinScreen(pathname)) return CHECKIN_IDLE_SECONDS;
  return KIOSK_IDLE_SECONDS;
};

// Where to send the patient back to when a shared/kiosk device goes idle.
// Each destination is itself excluded from the in-scope check below, so there's
// no redirect loop once we land on it.
const resolveIdleReset = (pathname) => {
  if (pathname.startsWith('/mobile/')) return '/mobile/idle';
  if (isHandoffScreen(pathname)) return '/kiosk/idle';
  return '/kiosk';
};

// Single, app-wide guard (mounted once in App.jsx). It decides whether the
// current screen belongs to a shared/kiosk device that must auto-logout on
// inactivity, so individual screens never have to opt in.
const AutoIdleWarper = () => {
  const { pathname } = useLocation();

  // `myriadIsKiosk` is set by KioskContextProvider on a physical kiosk and cleared on a
  // mobile/tablet kiosk (which is covered by its own /mobile/ screens). It is never set on
  // a patient's personal device, so personal instant links, own-device check-in and a
  // patient paying their own invoice link all stay excluded.
  const inScope =
    pathname.startsWith('/kiosk/') ||
    pathname.startsWith('/mobile/') ||
    (isKioskDevice() && isHandoffScreen(pathname));

  // Never run on the attract/idle/destination screens (prevents redirect loops).
  if (!inScope || pathname === '/kiosk' || pathname.includes('idle')) return null;

  return <AutoIdle resetTo={resolveIdleReset(pathname)} />;
};

const AutoIdle = ({ resetTo = '/kiosk' }) => {
  const { pathname } = useLocation();

  const [remaining, setRemaining] = useState(WARNING_SECONDS);
  const [showModal, setShowModal] = useState(false);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // The countdown runs from a setInterval that closes over the render it started on, so
  // without this a route change mid-countdown would log out (or skip it) for the screen the
  // patient already left.
  const idleScreenRef = useRef({ pathname, resetTo });
  idleScreenRef.current = { pathname, resetTo };

  const { reset } = useIdleTimer({
    timeout: resolveIdleSeconds(pathname) * 1000,
    onIdle: () => {
      setShowModal(true);
      requestApi({
        url: '/api/kiosk/idle_request',
        params: { status: 'idle', url: pathname },
        navigate
      });
      setRemaining(WARNING_SECONDS);
    },
    onActive: () => {
      setShowModal(false);
      if (showModal) {
        requestApi({
          url: '/api/kiosk/idle_request',
          params: { status: 'active', url: pathname },
          navigate
        });
      }
      reset();
    },
    onAction: reset,
    debounce: 500,
    events: ['keydown', 'mousedown', 'touchstart', 'touchmove', 'MSPointerDown', 'MSPointerMove']
  });

  const handleStillHere = () => {
    setShowModal(false);
    setRemaining(WARNING_SECONDS);
    reset();
  };

  // On the handoff and check-in screens the patient is signed in, so dropping the session
  // cookie is the only thing that keeps the next patient from resuming this one's check-in.
  // The storage/React Query purge is owned by the idle screen we land on (views/Idle.jsx),
  // so it deliberately isn't repeated here.
  const expireSession = async () => {
    const { pathname: idlePath, resetTo: idleResetTo } = idleScreenRef.current;
    const goToReset = () => navigate(idleResetTo);
    let loggingOut = false;

    try {
      if (!isHandoffScreen(idlePath) && !isCheckinScreen(idlePath)) return;

      await queryClient.cancelQueries();
      loggingOut = true;
      requestApi({
        url: '/api/user/logout',
        params: {},
        navigate,
        onSuccess: goToReset,
        onError: goToReset,
        onCatch: goToReset
      });
    } catch (error) {
      Honeybadger.notify(error, '[error]AutoIdle:expireSession');
    } finally {
      // requestApi routes every outcome to goToReset, so this only covers the paths that
      // never reached it - the patient must leave the screen either way.
      if (!loggingOut) goToReset();
    }
  };

  useEffect(() => {
    if (!showModal) return undefined;

    let secondsLeft = WARNING_SECONDS;
    setRemaining(secondsLeft);

    const interval = setInterval(() => {
      secondsLeft -= 1;
      setRemaining(secondsLeft);

      if (secondsLeft <= 0) {
        clearInterval(interval);
        setShowModal(false);
        reset();
        expireSession();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [showModal]);

  return (
    <Modal
      isOpen={showModal}
      handleClose={handleStillHere}
      disableHeader={true}
      shouldCloseOnOverlayClick={false}
      overlayClassName="z-[300]"
      isFooter={false}
      isCentered>
      <div className="my-10 flex flex-col items-center gap-8">
        <NotFound
          icon="illustration-automatization"
          title="Are you still there?"
          description={`For your privacy, we'll sign you out in ${remaining} ${
            remaining === 1 ? 'second' : 'seconds'
          } unless you let us know you're still here.`}
          className="pt-0"
        />
        <Button
          size="large"
          text="I'm still here"
          className="h-16 min-w-[280px] rounded-xl xs:h-14 xs:w-full"
          textClassName="text-xl xs:text-lg"
          onClick={handleStillHere}
        />
      </div>
    </Modal>
  );
};
export default AutoIdleWarper;
